import { motion, AnimatePresence } from "framer-motion";
import { X, Minus, Plus, Trash2, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { formatMoney, nodes } from "@/lib/shopify";

export default function CartDrawer() {
  const { cart, isOpen, closeCart, updateLine, removeLine, busy, cartError, count } = useCart();
  const lines = cart ? nodes(cart.lines) : [];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            data-testid="cart-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeCart}
            className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm"
          />
          <motion.aside
            data-testid="cart-drawer"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            className="fixed right-0 top-0 z-[80] flex h-full w-full max-w-md flex-col border-l border-white/10 bg-obsidian"
          >
            <div className="flex items-center justify-between border-b border-white/10 px-6 py-5">
              <h2 className="font-display text-lg font-extrabold text-ivory">
                Your stash {count > 0 && <span className="text-amber">({count})</span>}
              </h2>
              <button
                data-testid="cart-close-button"
                onClick={closeCart}
                className="rounded-full p-2 text-ivory/70 hover:bg-white/10 hover:text-amber"
                aria-label="Close cart"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-5">
              {lines.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center gap-4 text-center">
                  <p className="font-editorial text-2xl italic text-ivory/70">
                    Suspiciously empty.
                  </p>
                  <p className="text-sm text-ivory/40">
                    Your future favourite object is waiting.
                  </p>
                  <Link
                    to="/shop"
                    data-testid="cart-empty-shop-link"
                    onClick={closeCart}
                    className="mt-2 rounded-full bg-amber px-6 py-3 font-mono2 text-xs font-bold uppercase tracking-[0.2em] text-void hover:scale-[1.03]"
                  >
                    Go get weird
                  </Link>
                </div>
              ) : (
                <ul className="space-y-5">
                  {lines.map((line) => {
                    const m = line.merchandise;
                    return (
                      <li
                        key={line.id}
                        data-testid={`cart-line-${line.id.slice(-6)}`}
                        className="flex gap-4 border-b border-white/5 pb-5"
                      >
                        <Link
                          to={`/products/${m.product.handle}`}
                          onClick={closeCart}
                          className="h-24 w-20 shrink-0 overflow-hidden rounded-lg border border-white/10 bg-void"
                        >
                          {m.image?.url && (
                            <img
                              src={m.image.url}
                              alt={m.image.altText || m.product.title}
                              className="h-full w-full object-cover"
                            />
                          )}
                        </Link>
                        <div className="flex flex-1 flex-col">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <p className="font-display text-sm font-bold leading-snug text-ivory">
                                {m.product.title}
                              </p>
                              {m.title !== "Default Title" && (
                                <p className="mt-0.5 font-mono2 text-[10px] uppercase tracking-widest text-ivory/40">
                                  {m.title}
                                </p>
                              )}
                            </div>
                            <button
                              data-testid={`cart-remove-${line.id.slice(-6)}`}
                              onClick={() => removeLine(line.id)}
                              disabled={busy}
                              className="p-1 text-ivory/40 hover:text-amber"
                              aria-label="Remove item"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                          <div className="mt-auto flex items-center justify-between">
                            <div className="flex items-center rounded-full border border-white/15">
                              <button
                                data-testid={`cart-qty-minus-${line.id.slice(-6)}`}
                                onClick={() =>
                                  line.quantity > 1
                                    ? updateLine(line.id, line.quantity - 1)
                                    : removeLine(line.id)
                                }
                                disabled={busy}
                                className="p-1.5 px-2 text-ivory/60 hover:text-amber"
                                aria-label="Decrease quantity"
                              >
                                <Minus className="h-3.5 w-3.5" />
                              </button>
                              <span className="w-6 text-center font-mono2 text-xs font-bold text-ivory">
                                {line.quantity}
                              </span>
                              <button
                                data-testid={`cart-qty-plus-${line.id.slice(-6)}`}
                                onClick={() => updateLine(line.id, line.quantity + 1)}
                                disabled={busy}
                                className="p-1.5 px-2 text-ivory/60 hover:text-amber"
                                aria-label="Increase quantity"
                              >
                                <Plus className="h-3.5 w-3.5" />
                              </button>
                            </div>
                            <p className="font-mono2 text-sm font-bold text-amber">
                              {formatMoney(m.price)}
                            </p>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {lines.length > 0 && (
              <div className="border-t border-white/10 px-6 py-5">
                {cartError && (
                  <p data-testid="cart-error" className="mb-3 text-xs text-red-400">
                    {cartError}
                  </p>
                )}
                <div className="mb-4 flex items-center justify-between">
                  <span className="font-mono2 text-xs uppercase tracking-[0.25em] text-ivory/50">
                    Subtotal
                  </span>
                  <span data-testid="cart-subtotal" className="font-display text-xl font-extrabold text-ivory">
                    {formatMoney(cart.cost?.subtotalAmount)}
                  </span>
                </div>
                <p className="mb-4 text-xs text-ivory/40">
                  Shipping & taxes calculated at Shopify checkout.
                </p>
                <button
                  data-testid="cart-checkout-button"
                  onClick={() => (window.location.href = cart.checkoutUrl)}
                  className="group flex w-full items-center justify-center gap-2 rounded-full bg-amber py-4 font-mono2 text-xs font-bold uppercase tracking-[0.2em] text-void transition-all hover:shadow-[0_0_40px_rgba(255,92,0,0.35)]"
                >
                  Checkout via Shopify
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
